import { useEffect, useRef } from "react";

/**
 * Lightweight canvas particle field. Renders a slow, atmospheric drift of dim
 * gold points behind hero content — evoking signal → system → momentum. Fully
 * disabled on touch devices and when prefers-reduced-motion is set.
 */
export default function ParticleField({ className = "" }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Guard: skip on touch or reduced motion (perf + a11y)
    const isTouch = window.matchMedia("(hover: none)").matches;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isTouch || reduce) return;

    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let w = 0;
    let h = 0;
    const resize = () => {
      const r = canvas.parentElement.getBoundingClientRect();
      w = r.width;
      h = r.height;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const N = Math.min(120, Math.round((w * h) / 14000));
    const particles = Array.from({ length: N }).map(() => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.08,
      vy: (Math.random() - 0.5) * 0.08,
      r: 0.6 + Math.random() * 1.2,
      a: 0.15 + Math.random() * 0.35,
      hueShift: Math.random(),
    }));

    let t0 = performance.now();
    const draw = (now) => {
      const dt = Math.min(48, now - t0);
      t0 = now;
      ctx.clearRect(0, 0, w, h);

      // Draw thin connective threads between near-neighbors
      ctx.lineWidth = 0.5;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
      }
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 12000) {
            const a = (1 - d2 / 12000) * 0.09;
            ctx.strokeStyle = `rgba(201,168,76,${a})`;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
      }

      // Points
      for (const p of particles) {
        const isGold = p.hueShift < 0.35;
        const color = isGold
          ? `rgba(201,168,76,${p.a})`
          : `rgba(240,236,228,${p.a * 0.6})`;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    let rt;
    const onResize = () => {
      clearTimeout(rt);
      rt = setTimeout(resize, 180);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      clearTimeout(rt);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={`pointer-events-none absolute inset-0 ${className}`}
    />
  );
}
